import {
    type ViewStyle,
    StyleSheet
} from "react-native";
import type {
    AccordionTriggerDynamicStyleType
} from "./type";
import type {
    Mutable
} from "../../../../types";
import {
    getCornersStyle,
    getCornerStyle
} from "../../../../helpers/theme/corner";
import {
    getFocusRingStyle
} from "../../../../helpers/theme/state";
import {
    getStateLayerColor
} from "../../../stateLayer/stylesheet";
import {
    getHitArea
} from "../../../../helpers/theme/control";
import {
    getAccordionTriggerRadius
} from "../../stylesheet";
import {
    webStyle
} from "../../../../utils";

const stylesheet = StyleSheet.create({
    container: {
        justifyContent: "center",
        ...webStyle({
            outlineStyle: "solid",
            outlineWidth: 0
        })
    },
    content: {
        alignItems: "flex-start",
        flexDirection: "row"
    },
    textBlock: {
        flexDirection: "column",
        flexShrink: 1,
        flexGrow: 1,
        minWidth: 0
    },
    header: {
        flexDirection: "column"
    },
    layer: {
        pointerEvents: "none",
        position: "absolute"
    },
    meta: {
        alignItems: "center",
        flexDirection: "row",
        flexShrink: 0
    },
    icon: {
        flexShrink: 0
    },
    layerFill: {
        bottom: 0,
        start: 0,
        end: 0,
        top: 0
    }
});

export const useStyles = ({
    currentMeasures,
    isFocusVisible,
    shapeConfigs,
    touchTargets,
    pointerType,
    isDisabled,
    isHovered,
    isPressed,
    radiuses,
    borders,
    variant,
    colors,
    layout,
    spaces,
    isOpen
}: AccordionTriggerDynamicStyleType) => {
    const hitArea = getHitArea({
        visualSize: currentMeasures.minHeight,
        touchTargets,
        pointerType
    });

    const iconOffset = (currentMeasures.lineHeight - currentMeasures.iconSize) / 2;

    const styles = {
        content: {
            paddingHorizontal: currentMeasures.paddingHorizontal,
            paddingVertical: currentMeasures.paddingVertical,
            gap: currentMeasures.gap
        } as Mutable<ViewStyle>,
        layer: {
            backgroundColor: getStateLayerColor({
                overlayColor: "default",
                isHovered,
                isPressed,
                colors
            })
        } as Mutable<ViewStyle>,
        meta: {
            height: currentMeasures.lineHeight,
            gap: spaces.spacingSm
        } as Mutable<ViewStyle>,
        container: {
            minHeight: hitArea.minHeight
        } as Mutable<ViewStyle>,
        textBlock: {
            gap: spaces.spacingXxs
        } as Mutable<ViewStyle>,
        icon: {
            marginTop: iconOffset
        } as Mutable<ViewStyle>,
        chevron: {
            marginTop: iconOffset
        } as Mutable<ViewStyle>
    };

    if(variant === "plain") {
        Object.assign(styles.layer, getCornerStyle({
            radius: radiuses.actions,
            shapeConfigs
        }), {
            bottom: spaces.spacingXxs,
            start: -spaces.spacingSm,
            top: spaces.spacingXxs,
            end: -spaces.spacingSm
        });
    } else {
        const triggerRadius = getAccordionTriggerRadius({
            radiuses,
            borders,
            variant,
            layout,
            isOpen
        });

        Object.assign(styles.layer, stylesheet.layerFill, getCornersStyle({
            corners: {
                borderEndStartRadius: triggerRadius.bottom,
                borderStartStartRadius: triggerRadius.top,
                borderEndEndRadius: triggerRadius.bottom,
                borderStartEndRadius: triggerRadius.top
            },
            shapeConfigs
        }));
    }

    Object.assign(styles.layer, getFocusRingStyle({
        isInset: variant !== "plain",
        isFocusVisible,
        borders,
        colors
    }));

    if(isDisabled) {
        Object.assign(styles.container, webStyle({
            cursor: "not-allowed"
        }));
    }

    return styles;
};
export default stylesheet;
